import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../lib/instant";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { id as instantId } from "../lib/instant";
import toast from "react-hot-toast";
import { CheckCircle, Upload, MapPin, User, Phone, Mail } from "lucide-react";
import CouponInput from "../components/CouponInput";
import { sendOrderConfirmation } from "../lib/email";

const STEPS = ["Address", "Review", "Payment", "Confirm"];

export default function Checkout({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const navigate = useNavigate();
  const { cart, cartSubtotal, clearCart } = useCart();
  const { user } = useAuth();

  const [step, setStep]                     = useState(0);
  const [loading, setLoading]               = useState(false);
  const [orderId, setOrderId]               = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [coupon, setCoupon]                 = useState(null);
  const [selectedAddrId, setSelectedAddrId] = useState(null); // which saved address is selected

  const [form, setForm] = useState({
    name: "", email: user?.email || "", phone: "",
    address: "", city: "", state: "", pincode: "", notes: "",
  });

  // Load settings + saved addresses
  const { data } = db.useQuery({
    settings: {},
    addresses: user ? { $: { where: { userId: user.id } } } : {},
  });
  const settings        = data?.settings?.[0]  || {};
  const savedAddresses  = data?.addresses      || [];

  const gstRate         = settings.gstRate || 5;
  const gstAmount       = (cartSubtotal * gstRate) / 100;
  const deliveryFee     = cartSubtotal >= (settings.freeDeliveryAbove || 500) ? 0 : 50;
  const couponDiscount  = coupon?.discount || 0;
  const total           = cartSubtotal + gstAmount + deliveryFee - couponDiscount;

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // When user clicks a saved address — fill the form
  const applySavedAddress = (addr) => {
    setSelectedAddrId(addr.id);
    setForm(f => ({
      ...f,
      name:    addr.name    || f.name,
      phone:   addr.phone   || f.phone,
      address: addr.address || "",
      city:    addr.city    || "",
      state:   addr.state   || "",
      pincode: addr.pincode || "",
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setScreenshotPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const placeOrder = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const oid = instantId();
      const orderData = {
        userId: user.id,
        userEmail: user.email,
        customerName: form.name,
        phone: form.phone,
        address: `${form.address}, ${form.city}, ${form.state} - ${form.pincode}`,
        notes: form.notes,
        items: JSON.stringify(cart.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, weight: i.weight }))),
        subtotal: cartSubtotal, gstRate, gstAmount, deliveryFee,
        couponCode: coupon?.code || "", couponDiscount, total,
        status: "Pending Payment", paymentMethod: "GPay",
        paymentScreenshot: screenshotPreview || "",
        createdAt: new Date().toISOString(),
      };
      await db.transact([db.tx.orders[oid].update(orderData)]);
      setOrderId(oid);
      clearCart();
      setStep(3);
      // Send email in background — never blocks order
      sendOrderConfirmation({ id: oid, ...orderData }).catch(console.warn);
    } catch (err) {
      toast.error("Failed to place order. Please try again.");
      console.error(err);
    }
    setLoading(false);
  };

  if (!user) return (
    <div style={{ paddingTop: 100, textAlign: "center", minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <h2 style={{ color: "#2c1810", fontFamily: "Georgia, serif" }}>Please log in to checkout</h2>
      <Link to="/login" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Login</Link>
    </div>
  );

  if (cart.length === 0 && step !== 3) return (
    <div style={{ paddingTop: 100, textAlign: "center", minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <h2 style={{ color: "#999", fontFamily: "Georgia, serif" }}>Your cart is empty</h2>
      <Link to="/shop" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Shop Now</Link>
    </div>
  );

  const inputStyle = { width: "100%", padding: "11px 14px", border: "2px solid rgba(107,33,24,0.15)", borderRadius: 10, fontSize: "0.9rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3" }}>
      {/* Header + stepper */}
      <div style={{ background: primaryColor, padding: "36px 5vw 28px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: "#fff", marginBottom: 24 }}>Checkout</h1>
          <div style={{ display: "flex" }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ flex: 1, textAlign: "center" }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", margin: "0 auto 5px", background: i <= step ? accentColor : "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: i <= step ? "#2c1810" : "rgba(255,255,255,0.5)", fontWeight: 700, fontSize: "0.8rem" }}>
                  {i < step ? "✓" : i + 1}
                </div>
                <div style={{ fontSize: "0.68rem", color: i <= step ? accentColor : "rgba(255,255,255,0.4)", fontWeight: i === step ? 700 : 400 }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 5vw" }}>

        {/* ── STEP 0: Address ── */}
        {step === 0 && (
          <div style={{ background: "#fff", borderRadius: 20, padding: 32 }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: "#2c1810", marginBottom: 20 }}>Delivery Address</h2>

            {/* Saved addresses */}
            {savedAddresses.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: primaryColor, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  📍 Your Saved Addresses
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {savedAddresses.map(addr => (
                    <div
                      key={addr.id}
                      onClick={() => applySavedAddress(addr)}
                      style={{
                        padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                        border: `2px solid ${selectedAddrId === addr.id ? primaryColor : "#e0d8d0"}`,
                        background: selectedAddrId === addr.id ? `${primaryColor}08` : "#fdf6e3",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        transition: "all 0.15s",
                      }}
                    >
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          {addr.label && <span style={{ background: primaryColor, color: "#fff", padding: "2px 8px", borderRadius: 10, fontSize: "0.65rem", fontWeight: 700 }}>{addr.label}</span>}
                          <span style={{ fontWeight: 700, color: "#2c1810", fontSize: "0.88rem" }}>{addr.name}</span>
                        </div>
                        <div style={{ fontSize: "0.82rem", color: "#666" }}>
                          {addr.address}, {addr.city} {addr.state && `- ${addr.state}`} {addr.pincode && `- ${addr.pincode}`}
                        </div>
                        {addr.phone && <div style={{ fontSize: "0.75rem", color: "#999", marginTop: 2 }}>📞 {addr.phone}</div>}
                      </div>
                      {selectedAddrId === addr.id && (
                        <CheckCircle size={20} color={primaryColor} style={{ flexShrink: 0, marginLeft: 12 }} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 4px" }}>
                  <div style={{ flex: 1, height: 1, background: "#e0d8d0" }} />
                  <span style={{ fontSize: "0.75rem", color: "#aaa", whiteSpace: "nowrap" }}>or enter a new address</span>
                  <div style={{ flex: 1, height: 1, background: "#e0d8d0" }} />
                </div>
              </div>
            )}

            {/* Address form */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { name: "name",    label: "Full Name *",    placeholder: "Your full name",       full: false },
                { name: "phone",   label: "Phone Number *", placeholder: "+91 98XXX XXXXX",      full: false },
                { name: "email",   label: "Email",          placeholder: "your@email.com",       full: true  },
                { name: "address", label: "Street Address *",placeholder: "Street address, Area", full: true  },
                { name: "city",    label: "City *",          placeholder: "City",                 full: false },
                { name: "state",   label: "State",           placeholder: "Tamil Nadu",           full: false },
                { name: "pincode", label: "PIN Code *",      placeholder: "600001",               full: false },
              ].map(f => (
                <div key={f.name} style={{ gridColumn: f.full ? "1/-1" : "auto" }}>
                  <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 700, color: primaryColor, marginBottom: 5 }}>{f.label}</label>
                  <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} style={inputStyle} />
                </div>
              ))}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 700, color: primaryColor, marginBottom: 5 }}>Order Notes (Optional)</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Any special instructions..." style={{ ...inputStyle, resize: "vertical" }} />
              </div>
            </div>

            <button
              onClick={() => {
                if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
                  toast.error("Please fill Name, Phone, Address, City and PIN Code");
                  return;
                }
                setStep(1);
              }}
              style={{ marginTop: 22, background: primaryColor, color: "#fff", padding: "13px 36px", border: "none", borderRadius: 12, fontWeight: 700, fontSize: "1rem", cursor: "pointer", width: "100%" }}
            >Continue to Review →</button>
          </div>
        )}

        {/* ── STEP 1: Review ── */}
        {step === 1 && (
          <div>
            <div style={{ background: "#fff", borderRadius: 20, padding: 28, marginBottom: 16 }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: "#2c1810", marginBottom: 20 }}>Order Review</h2>
              {cart.map(item => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0e8d0" }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#2c1810" }}>{item.name}</div>
                    <div style={{ fontSize: "0.78rem", color: "#999" }}>Qty: {item.quantity} × ₹{item.price}</div>
                  </div>
                  <div style={{ fontWeight: 800, color: primaryColor }}>₹{(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}

              {/* Coupon */}
              <div style={{ marginTop: 18, marginBottom: 14 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#555", marginBottom: 8 }}>Have a coupon?</div>
                <CouponInput subtotal={cartSubtotal} onApply={setCoupon} theme={theme} />
              </div>

              {/* Bill */}
              <div style={{ borderTop: "2px solid #f0e8d0", paddingTop: 14 }}>
                {[
                  ["Subtotal", `₹${cartSubtotal.toFixed(2)}`],
                  [`GST (${gstRate}%)`, `₹${gstAmount.toFixed(2)}`],
                  ["Delivery", deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`],
                  ...(couponDiscount > 0 ? [[`Discount (${coupon?.code})`, `-₹${couponDiscount.toFixed(2)}`]] : []),
                ].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.88rem", color: "#555" }}>
                    <span>{l}</span>
                    <span style={{ fontWeight: 700, color: v === "FREE" || v.startsWith("-") ? secondaryColor : "#333" }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.15rem", fontWeight: 800, color: "#2c1810", paddingTop: 10, borderTop: "2px solid #f0e8d0" }}>
                  <span>Total</span><span>₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: 20, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, color: "#2c1810", marginBottom: 4 }}>📍 Delivering to:</div>
                  <div style={{ fontSize: "0.88rem", color: "#555", lineHeight: 1.6 }}>
                    {form.name} · {form.phone}<br />
                    {form.address}, {form.city}{form.state ? `, ${form.state}` : ""} - {form.pincode}
                  </div>
                </div>
                <button onClick={() => setStep(0)} style={{ background: "none", border: `1.5px solid ${primaryColor}`, color: primaryColor, padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                  Edit
                </button>
              </div>
            </div>
            <button onClick={() => setStep(2)} style={{ width: "100%", background: primaryColor, color: "#fff", padding: "13px", border: "none", borderRadius: 12, fontWeight: 700, fontSize: "1rem", cursor: "pointer" }}>
              Continue to Payment →
            </button>
          </div>
        )}

        {/* ── STEP 2: Payment ── */}
        {step === 2 && (
          <div style={{ background: "#fff", borderRadius: 20, padding: 32 }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.4rem", color: "#2c1810", marginBottom: 6 }}>Pay via GPay / UPI</h2>
            <p style={{ color: "#888", marginBottom: 28, fontSize: "0.88rem" }}>Scan the QR code and pay ₹{total.toFixed(2)}, then upload your payment screenshot.</p>

            {/* QR Code */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ display: "inline-block", padding: 16, background: "#f9f9f9", borderRadius: 16, border: "2px solid #f0e8d0" }}>
                {settings.gpayQrUrl ? (
                  <img src={settings.gpayQrUrl} alt="GPay QR" style={{ width: 200, height: 200, objectFit: "contain", borderRadius: 8 }} />
                ) : (
                  <div style={{ width: 200, height: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#bbb" }}>
                    <div style={{ fontSize: "3rem" }}>📱</div>
                    <div style={{ fontSize: "0.78rem", textAlign: "center" }}>GPay QR<br/>(Set in Admin → Settings)</div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 12, background: "#fdf6e3", padding: "10px 20px", borderRadius: 10, display: "inline-block" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: "1.5rem", fontWeight: 800, color: primaryColor }}>₹{total.toFixed(2)}</div>
                <div style={{ fontSize: "0.75rem", color: "#888" }}>Amount to pay</div>
              </div>
            </div>

            {/* Screenshot upload */}
            <div
              onClick={() => document.getElementById("ss-input").click()}
              style={{ border: "2px dashed #d0c8c0", borderRadius: 14, padding: 24, textAlign: "center", cursor: "pointer", background: "#fdf6e3", marginBottom: 20 }}
            >
              <input id="ss-input" type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
              {screenshotPreview ? (
                <div>
                  <img src={screenshotPreview} alt="Screenshot" style={{ maxHeight: 180, borderRadius: 10, marginBottom: 8 }} />
                  <div style={{ fontSize: "0.82rem", color: secondaryColor, fontWeight: 700 }}>✓ Screenshot uploaded · tap to change</div>
                </div>
              ) : (
                <div>
                  <Upload size={32} color="#ccc" style={{ marginBottom: 10 }} />
                  <div style={{ fontWeight: 700, color: "#555", marginBottom: 4 }}>Upload Payment Screenshot</div>
                  <div style={{ fontSize: "0.78rem", color: "#aaa" }}>Tap to upload your GPay payment confirmation</div>
                </div>
              )}
            </div>

            <div style={{ background: "#fff8e1", padding: 14, borderRadius: 10, marginBottom: 20, fontSize: "0.82rem", color: "#795548", border: "1px solid #ffe082" }}>
              ⚠️ Your order will be confirmed after our team verifies your payment screenshot.
            </div>

            <button onClick={placeOrder} disabled={loading} style={{ width: "100%", background: loading ? "#ccc" : secondaryColor, color: "#fff", padding: "13px", border: "none", borderRadius: 12, fontWeight: 700, fontSize: "1rem", cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "Placing Order..." : "Place Order"}
            </button>
          </div>
        )}

        {/* ── STEP 3: Confirmed ── */}
        {step === 3 && (
          <div style={{ textAlign: "center", background: "#fff", borderRadius: 20, padding: "56px 32px" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <CheckCircle size={36} color={secondaryColor} />
            </div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.9rem", color: "#2c1810", marginBottom: 10 }}>Order Placed!</h2>
            <p style={{ color: "#888", marginBottom: 6 }}>Your order has been placed successfully.</p>
            <p style={{ color: "#888", fontSize: "0.88rem", marginBottom: 6 }}>
              Order ID: <strong style={{ color: primaryColor, fontFamily: "monospace" }}>#{orderId?.slice(0,8).toUpperCase()}</strong>
            </p>
            <p style={{ color: "#888", fontSize: "0.85rem", marginBottom: 4 }}>
              A confirmation email will be sent to <strong>{user?.email}</strong>
            </p>
            <div style={{ background: "#fff8e1", padding: 14, borderRadius: 12, margin: "20px auto", maxWidth: 400, fontSize: "0.85rem", color: "#795548" }}>
              📦 We will verify your payment and confirm your order shortly.
            </div>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/orders" style={{ background: primaryColor, color: "#fff", padding: "12px 26px", borderRadius: 12, textDecoration: "none", fontWeight: 700 }}>Track My Order</Link>
              <Link to="/shop" style={{ border: `2px solid ${primaryColor}`, color: primaryColor, padding: "12px 26px", borderRadius: 12, textDecoration: "none", fontWeight: 700 }}>Continue Shopping</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
